import { resolveI18nTree } from './i18nTree';

function isPlainObject(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Kept as a workpage-specific alias of the generic tree resolver (moved to
// i18nTree.ts so FormComponent can reuse the same convention for schema/uiSchema).
export const localizeWorkpageElement = resolveI18nTree;

// Shallow counterpart for records that carry their own display fields alongside
// a heavy nested payload (e.g. a Workpage's `name`, shown in the sidebar/breadcrumbs,
// next to its full `definition` tree). Only resolves top-level fields of the
// RECORD — it never recurses into nested objects/arrays — so localizing a list
// of records (most of which the user never opens) doesn't deep-clone every one
// of their definitions.
//
// The translation map itself lives at `record.definition.i18n`, NOT as a
// sibling of `name` — the backend's Workpage DTO has explicit typed properties
// for `name`/`slug`/`isAdmin`/etc. (see WorkpageInfoDTO/UpdateDashboardDTO in
// UWEBackend) and only `definition` is an opaque JSON blob. A sibling `i18n`
// field would silently fail to persist; nesting it inside `definition` rides
// along with the blob that already round-trips untouched.
export function localizeWorkpageRecord<T extends { definition?: { i18n?: Record<string, Record<string, any>> } }>(record: T, locale: string): T {
  const translations = record?.definition?.i18n;

  if (!isPlainObject(record) || !isPlainObject(translations)) {
    return record;
  }

  const result: Record<string, any> = { ...record };

  for (const [key, byLocale] of Object.entries(translations)) {
    if (isPlainObject(byLocale) && byLocale[locale] != null) {
      result[key] = byLocale[locale];
    }
  }

  return result as T;
}
